/**
 * <SubjectBadge> — one subject chip (atom, L0.1).
 *
 * Display (no `onSelect`): outlined chip — subject color on border/label, neutral fill.
 * Selectable (`onSelect` set): pressable pill — filled when `selected`, otherwise raised + border.
 * Choisie, la puce est pleine de la couleur de la matière : son picto se pose sur sa pastille
 * claire (`<ImageIcon plate>`), sans quoi le « √ » marine des maths disparaît sur un aplat marine.
 * `size` only drives geometry via `SIZE_TOKENS` (icon / text / gap / padding / radius / border).
 * Label truncation (4-char) applies to display chips; selectable chips — and `fullLabel` — show
 * the full name.
 *
 * `<SubjectBadgeOverflow>` est sa sœur : le « +N » qui ferme une pile de matières, dans la même
 * boîte qu'une chip d'affichage sans libellé ; `<SubjectBadgeSkeleton>` en tient la place pendant
 * un chargement.
 */
import React from "react"
import { Pressable, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type FontSizeKey } from "../../theme/tokens/typography"
import { type RadiusKey } from "../../theme/tokens/radius"
import { Row } from "../containers/Row"
import { ImageIcon } from "../icons/ImageIcon"
import { Text } from "../typography/Text"
import { type UISubject } from "../../../ports/primary/ui/types"

export type SubjectBadgeSize = "sm" | "md"

export type SubjectBadgeProps = {
    subject: UISubject
    /** Show the subject label next to the icon. Defaults to false. */
    showLabel?: boolean
    /**
     * Nommer la matière EN ENTIER. Défaut `false` : une chip d'AFFICHAGE abrège à quatre lettres,
     * parce qu'elle vit sur des cartes denses. Le résumé d'un formulaire, lui, a la place — et
     * « Math » y serait une abréviation que le parent n'a pas demandée.
     */
    fullLabel?: boolean
    /** Geometry only — defaults to `sm`. */
    size?: SubjectBadgeSize
    /** Selected fill (only meaningful when `onSelect` is set). */
    selected?: boolean
    /** Makes the chip selectable. Omit for display-only. */
    onSelect?: () => void
    disabled?: boolean
    /** Extra style — used by `<SubjectBadges>` to overlap chips. */
    style?: StyleProp<ViewStyle>
    testID?: string
}

/** La géométrie d'une chip par taille — la chip, le « +N » et le squelette la lisent. */
export const SIZE_TOKENS: Record<
    SubjectBadgeSize,
    {
        icon: number
        text: FontSizeKey
        gap: SpacingKey
        paddingV: number
        paddingH: number
        radius: RadiusKey
        borderWidth: number
    }
> = {
    sm: { icon: 12, text: "text-xs", gap: "1", paddingV: 2, paddingH: 3, radius: "sm", borderWidth: 1 },
    md: { icon: 22, text: "text-base", gap: "2", paddingV: 8, paddingH: 12, radius: "full", borderWidth: 2 },
}

/**
 * La boîte d'une chip d'affichage SANS libellé — le picto, son retrait, sa bordure. C'est la place
 * que tient le squelette d'une ligne qui la porte (`<ActivityRowSkeleton>`).
 */
export function subjectBadgeIconBox(size: SubjectBadgeSize = "sm"): { width: number; height: number } {
    const { icon, paddingV, paddingH, borderWidth } = SIZE_TOKENS[size]
    return { width: icon + 2 * (paddingH + borderWidth), height: icon + 2 * (paddingV + borderWidth) }
}

export function SubjectBadge(props: SubjectBadgeProps) {
    const {
        subject,
        showLabel = false,
        fullLabel = false,
        size = "sm",
        selected = false,
        onSelect,
        disabled = false,
        style,
        testID,
    } = props

    const selectable = onSelect != null
    const dims = SIZE_TOKENS[size]
    const label = selectable || !showLabel || fullLabel ? subject.name : subject.name.slice(0, 4)
    const labelColor: SemanticColorKey = selectable ? (selected ? "text-on-primary" : "text-primary") : subject.color

    const content = (
        <Row
            gap={dims.gap}
            align="center"
            style={[
                styles.chip(
                    dims.paddingV,
                    dims.paddingH,
                    dims.radius,
                    dims.borderWidth,
                    subject.color,
                    selectable,
                    selected,
                    disabled
                ),
                style,
            ]}
            testID={selectable ? undefined : testID}
        >
            <ImageIcon uri={subject.iconUrl} size={dims.icon} plate={selectable && selected} />
            {showLabel && (
                <Text size={dims.text} weight={selectable && selected ? "bold" : "semibold"} color={labelColor}>
                    {label}
                </Text>
            )}
        </Row>
    )

    if (!selectable) return content

    return (
        <Pressable
            testID={testID}
            disabled={disabled}
            onPress={onSelect}
            accessibilityRole="button"
            accessibilityState={{ selected, disabled }}
            style={({ pressed }) => [pressed && !disabled ? styles.pressed : undefined]}
        >
            {content}
        </Pressable>
    )
}

export type SubjectBadgeOverflowProps = {
    /** Les matières que la pile ne montre pas. */
    count: number
    /** Extra style — used by `<SubjectBadges>` to overlap it. */
    style?: StyleProp<ViewStyle>
}

/**
 * « +4 » — les matières qu'une pile ne montre pas (`<SubjectBadges>` le pose). Il prend la boîte
 * d'une chip d'affichage `sm` sans libellé, celle des piles (`subjectBadgeIconBox`) : même
 * hauteur, même rayon, même bordure, et au moins sa largeur. Neutre, parce qu'il ne dit aucune matière en particulier : le
 * contour du livre (`border`) et l'encre secondaire sur l'encart (`text-muted` sur `surface-subtle`,
 * une paire mesurée AA dans les deux thèmes).
 */
export function SubjectBadgeOverflow(props: SubjectBadgeOverflowProps) {
    const { count, style } = props
    const dims = SIZE_TOKENS.sm
    const box = subjectBadgeIconBox("sm")

    // La feuille d'une chip d'affichage, au contour du livre ; la boîte, elle, est fixée.
    return (
        <View
            style={[
                styles.chip(dims.paddingV, dims.paddingH, dims.radius, dims.borderWidth, "border", false, false, false),
                styles.overflowBox(box.width, box.height),
                style,
            ]}
        >
            <Text size={dims.text} weight="bold" color="text-muted">{`+${count}`}</Text>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    overflowBox: (minWidth: number, height: number) => ({
        minWidth,
        height,
        paddingVertical: 0,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
    chip: (
        paddingV: number,
        paddingH: number,
        radius: RadiusKey,
        borderWidth: number,
        color: SemanticColorKey,
        selectable: boolean,
        selected: boolean,
        disabled: boolean
    ) => ({
        paddingVertical: paddingV,
        paddingHorizontal: paddingH,
        borderRadius: theme.radius[radius],
        borderWidth,
        borderColor: selectable ? (selected ? theme.colors[color] : theme.colors.border) : theme.colors[color],
        backgroundColor: selectable
            ? selected
                ? theme.colors[color]
                : theme.colors["surface-raised"]
            : theme.colors["surface-subtle"],
        opacity: selectable && disabled ? 0.5 : 1,
    }),
    pressed: { opacity: 0.85 },
}))
